import { memo, ReactNode } from 'react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BaseSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string; // REQUIRED: Used for accessibility (DialogTitle). Can be hidden visually if needed.
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  loading?: boolean;
  error?: string | null;
  showCloseButton?: boolean;
  hideTitle?: boolean; // Hide title visually while keeping it for accessibility
  className?: string;
  contentClassName?: string;
  overlayClassName?: string;
  preventOutsideClick?: boolean;
}

const sizeClasses = {
  sm: 'w-[300px] sm:w-[400px]',
  md: 'w-full sm:max-w-lg',
  lg: 'w-full sm:max-w-2xl',
  xl: 'w-full sm:max-w-4xl',
  full: 'w-full',
};

export const BaseSheet = memo(({
  isOpen,
  onClose,
  title = 'Sheet', // REQUIRED: Default fallback for accessibility
  description,
  children,
  footer,
  side = 'right',
  size = 'md',
  loading = false,
  error = null,
  showCloseButton = true,
  hideTitle = false,
  className,
  contentClassName,
  overlayClassName,
  preventOutsideClick = false,
}: BaseSheetProps) => {
  const handleOpenChange = (open: boolean) => {
    if (!open && !loading && !preventOutsideClick) {
      onClose();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side={side}
        overlayClassName={overlayClassName}
        showClose={false}
        className={cn(
          sizeClasses[size],
          'flex flex-col overflow-hidden',
          contentClassName
        )}
        onPointerDownOutside={(e) => {
          if (preventOutsideClick || loading) {
            e.preventDefault();
          }
        }}
      >
        {/* ACCESSIBILITY: Both DialogTitle and DialogDescription required by Radix UI */}
        {hideTitle ? (
          <VisuallyHidden>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{description || title}</SheetDescription>
          </VisuallyHidden>
        ) : (
          title && (
            <SheetHeader className="space-y-2">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <SheetTitle>{title}</SheetTitle>
                  <SheetDescription>{description || title}</SheetDescription>
                </div>
                {showCloseButton && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    disabled={loading}
                    className="rounded-full"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </SheetHeader>
          )
        )}
        
        <div className={cn('flex-1 overflow-y-auto py-4', className)}>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : (
            children
          )}
        </div>
        
        {footer && (
          <SheetFooter className="mt-auto">
            {footer}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
});

BaseSheet.displayName = 'BaseSheet';

// Export the common footer component from BaseModal
export { ModalFooter as SheetFooter } from './BaseModal';
